import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, Row, Col, Form } from 'react-bootstrap';
import { formatCurrency } from '../../shared/utils/currency';
import MonthlyAssetComparisonChart from './MonthlyAssetComparisonChart';
import SubtypeDistributionChart from './SubtypeDistributionChart';
import NetWorthTrendChart from './NetWorthTrendChart';
import AssetAllocationChart from './AssetAllocationChart';
import AssetGrowthChart from './AssetGrowthChart';
import PerHoldingTrendChart from './PerHoldingTrendChart';
import { Wallet2, GraphUp, GraphDown } from 'react-bootstrap-icons';
import './AssetDashboard.css'; // Import new CSS file

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const AssetDashboard = ({ activeProfileId, currency, selectedYears }) => {
  const [totalLatestAssetValue, setTotalLatestAssetValue] = useState(0);
  const [totalAssetValue, setTotalAssetValue] = useState(0);
  const [totalDebtValue, setTotalDebtValue] = useState(0);
  const [monthlyAssetSummary, setMonthlyAssetSummary] = useState([]);
  const [error, setError] = useState('');
  const [selectedAssetType, setSelectedAssetType] = useState('');

  const fetchTotalAssetValue = useCallback(async () => {
    if (!activeProfileId) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/api/assets/profiles/${activeProfileId}/assets/total_latest_value`);
      setTotalLatestAssetValue(response.data.total_latest_asset_value || 0);
      setTotalAssetValue(response.data.total_asset_value || 0);
      setTotalDebtValue(response.data.total_debt_value || 0);
      setError('');
    } catch (err) {
      console.error('Error fetching total latest asset value:', err);
      setError('Failed to fetch total asset value.');
    }
  }, [activeProfileId]);

  const fetchMonthlyAssetSummary = useCallback(async () => {
    if (!activeProfileId) return;
    try {
      if (selectedYears && selectedYears.length > 0) {
        const responses = await Promise.all(selectedYears.map(year =>
          axios.get(`${API_BASE_URL}/api/assets/profiles/${activeProfileId}/assets/monthly_summary`, { params: { year } })
        ));
        setMonthlyAssetSummary(responses.flatMap(r => r.data));
      } else {
        const response = await axios.get(`${API_BASE_URL}/api/assets/profiles/${activeProfileId}/assets/monthly_summary`);
        setMonthlyAssetSummary(response.data);
      }
      setError('');
    } catch (err) {
      console.error('Error fetching monthly asset summary:', err);
      setError('Failed to fetch monthly asset summary.');
    }
  }, [activeProfileId, selectedYears]);

  useEffect(() => {
    fetchTotalAssetValue();
    fetchMonthlyAssetSummary();
  }, [fetchTotalAssetValue, fetchMonthlyAssetSummary]);

  const assetTypes = Array.from(new Set(monthlyAssetSummary.map(item => item.AssetType)));

  useEffect(() => {
    if (assetTypes.length > 0 && !selectedAssetType) {
      setSelectedAssetType(assetTypes[0]);
    }
  }, [assetTypes, selectedAssetType]);

  return (
    <div className="asset-dashboard-container">
      {error && <p className="text-danger">{error}</p>}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="summary-card net-income-card asset-summary-card">
            <Card.Header>Net Asset Value</Card.Header>
            <Card.Body>
              <h5 className="card-title">{formatCurrency(totalLatestAssetValue, currency)}</h5>
              <Wallet2 size={32} />
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="summary-card income-card asset-summary-card">
            <Card.Header>Total Assets</Card.Header>
            <Card.Body>
              <h5 className="card-title">{formatCurrency(totalAssetValue, currency)}</h5>
              <GraphUp size={32} />
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="summary-card expenses-card asset-summary-card">
            <Card.Header>Total Debt</Card.Header>
            <Card.Body>
              <h5 className="card-title">{formatCurrency(totalDebtValue, currency)}</h5>
              <GraphDown size={32} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col md={12} className="mb-4">
          <NetWorthTrendChart data={monthlyAssetSummary} currency={currency} />
        </Col>
      </Row>
      <Row>
        <Col md={6} className="mb-4">
          <AssetAllocationChart data={monthlyAssetSummary} currency={currency} />
        </Col>
        <Col md={6} className="mb-4">
          <AssetGrowthChart data={monthlyAssetSummary} currency={currency} />
        </Col>
      </Row>
      <Row>
        <Col md={6} className="mb-4">
          <Card>
            <Card.Header>Monthly Asset Comparison</Card.Header>
            <Card.Body>
              <MonthlyAssetComparisonChart data={monthlyAssetSummary} currency={currency} />
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} className="mb-4">
          <Card>
            <Card.Header>Monthly Subtype Distribution</Card.Header>
            <Card.Body>
              <Form.Group as={Row} className="mb-3 align-items-center">
                <Form.Label column sm="4">
                  Select Asset Type:
                </Form.Label>
                <Col sm="8">
                  <Form.Select
                    value={selectedAssetType}
                    onChange={(e) => setSelectedAssetType(e.target.value)}
                  >
                    {assetTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
              {selectedAssetType && (
                <SubtypeDistributionChart
                  data={monthlyAssetSummary}
                  currency={currency}
                  assetType={selectedAssetType}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col md={12} className="mb-4">
          <Card>
            <Card.Header>Per-Holding Trend {selectedAssetType ? `(${selectedAssetType})` : ''}</Card.Header>
            <Card.Body>
              {selectedAssetType && (
                <PerHoldingTrendChart
                  data={monthlyAssetSummary}
                  currency={currency}
                  assetType={selectedAssetType}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AssetDashboard;
